import Hls from 'hls.js';

export interface HiveConfig {
    jwt: string;
    videoId: string;
    manifest: string
}
interface HiveStats {
    cdnRequests: number;
    cdnResponseBytes: number;
    cdnResponseErrors: number;
    cdnResponses: number;
    p2pPartnerTotalSentBytes: number;
    p2pRequests: number;
    p2pResponseBytes: number;
    p2pResponseErrors: number;
    p2pResponses: number;
    partners: Record<string, unknown>
    savings: number;
    uuid: string;
}

interface HivePlugin {
    initSession: (hiveConfig: HiveConfig) => Promise<{ manifest: string }>;
    destroyHivePlugin: () => void;
}

function getHiveHtml5() {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return window.HiveHtml5;
}

function getHiveModule() {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return window.HiveModule;
}

export function isHiveConfigValid(hiveConfig: HiveConfig): boolean {
    return Boolean(hiveConfig?.jwt && hiveConfig?.videoId && hiveConfig?.manifest);
}

export async function setupHive({
    hiveConfig,
    useCredentials,
    hls,
    src,
    video,
    loadSource,
    retriesLeft = 3
}: {
    hiveConfig: HiveConfig;
    useCredentials: boolean;
    hls: Hls | undefined;
    src: string;
    video: HTMLVideoElement,
    loadSource: (src: string) => Promise<string>,
    retriesLeft?: number
}): Promise<string> {
    console.log('[Hive] setupHive', hiveConfig);

    const HiveModule = getHiveModule();
    const HiveHtml5 = getHiveHtml5();

    if (!HiveModule || !HiveHtml5) {
        console.error('[Hive] HiveJS is not loaded');
        return loadSource(src);
    }

    let hivePlugin: HivePlugin | undefined = undefined;
    const onError = (error: Record<string, unknown>) => {
        if (error.severity === 0) {
            console.error('[Hive] low severity error occurred', error);
        } else if (error.severity === 1) { // HIGH
            console.error('[Hive] high severity error occurred, retrying...', error);
            if (retriesLeft > 0) {
                retriesLeft--;
                setupHive({ hiveConfig, useCredentials, hls, video, loadSource, retriesLeft, src });
            } else {
                console.error('[Hive] no retries left, disable HivePlugin and fall back to CDN', error);
                hivePlugin?.destroyHivePlugin();
                loadSource(src);
            }
        } else if (error.severity === 2) { // CRITICAL
            console.error('[Hive] critical error occurred, disable HivePlugin and fall back to CDN', error);
            hivePlugin?.destroyHivePlugin();
            loadSource(src);
        }
    };

    hivePlugin = createHivePlugin({ video, useCredentials, onError });

    if (!hivePlugin) {
        return loadSource(hiveConfig.manifest);
    }

    const { manifest } = await hivePlugin.initSession(hiveConfig);

    HiveModule.enable(hivePlugin, hls, Hls);

    return loadSource(manifest);
}

function createHivePlugin({ video, useCredentials, onError }: {video: HTMLVideoElement; useCredentials: boolean, onError: (error: Record<string, unknown>) => void}): HivePlugin | undefined {
    const HiveHtml5 = getHiveHtml5();

    let requestCache ;
    if (useCredentials) {
        requestCache = {
            headers: [
                'Cookie'
            ]
        };
    }

    const hivePluginOptions = {
        playerName: 'hlsjs',
        playerVersion: Hls.version,
        debugLevel: 'notice',
        hiveTechOrder: ['HiveJava', 'HiveJS', 'StatsJS'],
        isLowLatency: false,
        onError,
        // onActiveSession: (session: unknown) => console.debug('[Hive] onActiveSession', session),
        // onSessionStateChange: (stateChange: unknown) => console.debug('[Hive] onSessionStateChange', stateChange),
        HiveJS: {
            // onActiveSession: (session: unknown) => console.debug('[Hive] onActiveSession', session),
            requestCache,
            renderStatsCallback: (stats: HiveStats) => {
                if (!stats?.savings) {
                    return;
                }
                console.debug('[Hive] renderStatsCallback', stats);
            }
        }
    };

    return new HiveHtml5(video, hivePluginOptions);
}
